/**
 * JHA Network SFTP / file share workflow step types (ACME-1998).
 * LaunchDarkly: boolean `jhft_jconnect_features` (team-managed; fail-closed default false).
 * Optional VITE overrides for local dev.
 */

import {
	isJhaNetworkFileShareStorageType,
	isJhaNetworkSftpStorageType,
	isJhaNetworkStorageType,
	normalizeStorageTypeKey,
} from "../constants/jha-network-storage-type";
import { FeatureFlags } from "./launchdarkly-config";
import { isFeatureEnabled } from "./launchdarkly-helpers";
import { normalizeStepFunctionValue } from "./step-function-display";

/** FileCopy + FileMove (stored as filecopy) + FileDownload per ACME-1998 AC1. */
export function isJhaNetworkStorageAllowedForStepFunction(
	stepFunctionName: string | undefined | null
): boolean {
	const fn = normalizeStepFunctionValue(String(stepFunctionName ?? ""));
	if (!fn) {
		return true;
	}
	return fn === "filecopy" || fn === "filedownload";
}

function viteTriState(envKey: string): boolean | undefined {
	const raw = String(import.meta.env[envKey] ?? "").trim().toLowerCase();
	if (raw === "false" || raw === "0" || raw === "no") return false;
	if (raw === "true" || raw === "1" || raw === "yes") return true;
	return undefined;
}

/** Local override: primary `VITE_ENABLE_JHFT_JCONNECT_FEATURES`; legacy ACME-1998 keys still honored. */
function jhftJconnectFeaturesViteOverride(): boolean | undefined {
	const keys = [
		"VITE_ENABLE_JHFT_JCONNECT_FEATURES",
		"VITE_ENABLE_JHA_NETWORK_LOCATION_VISIBILITY",
		"VITE_ENABLE_JHA_NETWORK_LOCATION_SAVE",
		"VITE_ENABLE_JHA_NETWORK_LOCATION_RUN",
	];
	for (const key of keys) {
		const v = viteTriState(key);
		if (v !== undefined) return v;
	}
	return undefined;
}

/** When false, JHA Network storage types stay hidden and cannot be saved (visibility/save/run). */
export function isJhftJconnectFeaturesEnabled(): boolean {
	const v = jhftJconnectFeaturesViteOverride();
	if (v === false) return false;
	if (v === true) return true;
	return isFeatureEnabled(FeatureFlags.JCONNECT_FEATURES, false);
}

export function isJhaNetworkLocationVisibilityEnabled(): boolean {
	return isJhftJconnectFeaturesEnabled();
}

export function isJhaNetworkLocationSaveEnabled(): boolean {
	return isJhftJconnectFeaturesEnabled();
}

export function isJhaNetworkLocationRunEnabled(): boolean {
	return isJhftJconnectFeaturesEnabled();
}

export function isJhaNetworkLocationSaveAllowedForStorageType(storageType: string | undefined | null): boolean {
	if (!isJhaNetworkStorageType(storageType)) return true;
	return isJhaNetworkLocationSaveEnabled();
}

export function shouldShowJhaNetworkStorageInPicker(
	storageType: string | undefined | null,
	currentStepStorageType: string | undefined | null,
	stepFunctionName?: string | undefined | null
): boolean {
	const key = normalizeStorageTypeKey(storageType);
	if (!isJhaNetworkStorageType(key)) return true;
	const isCurrent = normalizeStorageTypeKey(currentStepStorageType) === key;
	if (!isJhaNetworkStorageAllowedForStepFunction(stepFunctionName) && !isCurrent) {
		return false;
	}
	if (isJhaNetworkLocationVisibilityEnabled()) return true;
	return isCurrent;
}

export function isJhaNetworkSftpRunAllowed(storageType: string | undefined | null): boolean {
	if (!isJhaNetworkSftpStorageType(storageType)) return false;
	return isJhaNetworkLocationRunEnabled();
}

/** File share has no SFTP-style test connection UI until Workflow Service defines a POST shape (AC7). */
export function isJhaNetworkFileShareRunAllowed(storageType: string | undefined | null): boolean {
	if (!isJhaNetworkFileShareStorageType(storageType)) return false;
	return false;
}
